// src/helpers/urls.ts

import { useEffect, useState } from 'react'
import { ImageCache } from '../../cache'

export async function getPublicImageUrl(
  path: string | undefined
): Promise<string> {
  if (!path) return ''

  // Remove any leading slash to ensure consistent formatting
  const cleanPath = path.replace(/^\//, '')

  // Create cache key from the clean path
  const cacheKey = `/${cleanPath}`

  // Try to get from cache first
  const cached = await ImageCache.get(cacheKey)
  if (cached) return cached

  // If not in cache, return the public path
  return cacheKey
}

// Clean up expired images periodically
setInterval(
  () => {
    ImageCache.clearOld()
  },
  60 * 60 * 1000
) // Check every hour

// Helper hook for components
export function useCachedPublicImage(
  path: string | undefined
): string | undefined {
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!path) {
      setCachedUrl(undefined)
      return
    }

    let isMounted = true

    const loadImage = async () => {
      // Clean the path
      const cleanPath = path.replace(/^\//, '')
      const publicUrl = `/${cleanPath}`

      // Try to get from cache first
      const cached = await ImageCache.get(publicUrl)
      if (cached && isMounted) {
        setCachedUrl(cached)
        return
      }

      // If not in cache, fetch and cache
      try {
        const response = await fetch(publicUrl)
        const clonedResponse = response.clone()
        await ImageCache.set(publicUrl, clonedResponse)

        if (isMounted) {
          setCachedUrl(URL.createObjectURL(await response.blob()))
        }
      } catch (error) {
        console.error('Error loading image:', error)
        if (isMounted) setCachedUrl(publicUrl) // Fallback to public URL
      }
    }

    loadImage()

    return () => {
      isMounted = false
    }
  }, [path])

  return cachedUrl
}
